import { FC, ReactElement, useContext, useMemo } from 'react';
import { CgChevronDown, CgCornerUpLeft } from 'react-icons/cg';
import { useNavigate } from 'react-router';
import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';

import { useFeatures } from '~/core/shared/contexts/features';
import { Permissions } from '~/core/shared/generated/Permissions';
import { useCan } from '~/core/shared/hooks/useCan';
import {
  buildComponent,
  isLockedApplication,
} from '~/core/shared/utils/general';
import { TenantDocumentDetailsViewAction } from '../../../config';
import { useAdminApplicationContext } from '../../../contexts/AdminApplicationContext';
import { LayoutContext } from '../../Layout.component';
import { DocumentDetailsViewContext } from '../Details.view';
import {
  ApplicationContext,
  DocumentDetailsViewActionProvider,
} from '../Layout.component';

export const ApplicationSubheader: FC = () => {
  const navigate = useNavigate();
  const { config } = useContext(DocumentDetailsViewContext);
  const { getFeature } = useFeatures();
  const { can, meetsRequirement } = useCan();
  const { displayData } = useAdminApplicationContext();
  const { selectedNavItem } = useContext(LayoutContext);

  const actionsByKey: Record<string, TenantDocumentDetailsViewAction> = {};
  const actionMenuItems: Record<string, ReactElement | undefined> = {};

  const { hooks } = useContext(ApplicationContext);
  // An empty hook list is a legitimate state (the backend only returns hooks
  // once the document has a status against the definition), so derive this
  // rather than waiting for one to arrive, matching how the parent layout
  // reads the same value.
  const locked = isLockedApplication(hooks);

  config.actions.forEach((action) => {
    if (action.feature && !getFeature(action.feature)?.enabled) {
      return;
    }
    if (action.permission && !meetsRequirement(action.permission)) {
      return;
    }
    actionsByKey[action.key] = action;
    actionMenuItems[action.key] = action.menuItemComponent ? (
      buildComponent(action.menuItemComponent, { key: action.key })
    ) : (
      <Menu.Item key={action.key} value={action.key}>
        {action.text || action.key}
      </Menu.Item>
    );
  });

  const applicationData = useMemo(
    () => [
      {
        label: 'Application ID',
        value: displayData?.entityShortId,
      },
      {
        label: 'Name',
        value: displayData?.entityDisplayName,
      },
      {
        label: 'Status',
        value: displayData?.statusDisplayName ?? 'Open',
      },
    ],
    [displayData],
  );

  if (config.actions.length === 0 && hooks.length === 0) {
    return null;
  }

  return (
    <Flex
      alignItems="center"
      bg="gray.50"
      borderBottom={1}
      borderColor="gray.200"
      borderStyle="solid"
      color="gray.600"
      justifyContent="space-between"
      minH="60px"
      px={8}
      py={2}
    >
      <Stack alignItems="center" direction="row" gap={6}>
        <Button
          borderColor="black"
          color="black"
          colorPalette="white"
          flexShrink={0}
          ml={5}
          mr={2}
          onClick={() => {
            if (selectedNavItem) navigate(selectedNavItem);
          }}
          size="sm"
        >
          <Icon as={CgCornerUpLeft} />
          Exit application
        </Button>

        {applicationData.map((item, key) => (
          // eslint-disable-next-line react/no-array-index-key
          <Text key={key} color="black">
            <b>{item.label}:</b> {item.value}
          </Text>
        ))}
      </Stack>

      {can(Permissions.ActionsView) && !locked ? (
        <Box position="relative">
          <Menu.Root lazyMount>
            <Menu.Trigger asChild>
              <Button
                color="black"
                colorPalette="white"
                fontWeight="normal"
                size="md"
                variant="outline"
              >
                Actions
                <CgChevronDown />
              </Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content position="relative">
                  {Object.entries(actionMenuItems).map((entry) => {
                    const action = actionsByKey[entry[0]];
                    const menuItemComponent = entry[1];
                    if (action && menuItemComponent) {
                      return (
                        <DocumentDetailsViewActionProvider
                          key={action.key}
                          action={action}
                        >
                          {menuItemComponent}
                        </DocumentDetailsViewActionProvider>
                      );
                    }
                    return null;
                  })}
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </Box>
      ) : null}
    </Flex>
  );
};
